import {CircularProgress} from "@mui/material";
import BaseDialog from "components/BaseDialog";
import formatBytes from "core/format/byteFormat";
import {useLocale, useMessages} from "i18n";
import {ImmediateDeliveryConsent} from "pay/ImmediateDeliveryConsent";
import {OrderTermsLine} from "pay/OrderTermsLine";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {type PaymentAdmission, withdrawalEffectOf} from "pay/withdrawalRight";
import type {PaidPlan} from "./PricingStorage";
import {planChangeCopy, planChangeWindowDays} from "./planChangeCopy";
import type {PlanChangeDirection} from "./upgradeAction";
import {Actions, Benefit, Body, ConfirmButton, OrderNoteSlot, QuietButton, State} from "./PlanChangeDialog.styles";

/** Preview lifecycle of the prorated amount: fetching, resolved, or the fetch failed. */
export type PlanPreviewStatus = "loading" | "ready" | "error";

/** What an upgrade states above its order button (`planChangeOrderNote`), its price already formatted. */
export interface OrderNoteFigures {
	/** The new plan's largest hosted upload in bytes, or null for a plan with no limit. */
	maxHostedUploadBytes: number | null;
	/** The new plan's monthly price, formatted, or null where it cannot be stated. */
	monthlyPrice: string | null;
}

/** The buyer's express request for immediate performance, as the hook holds it (kitecorp/transferify-ui#3457). */
export interface PlanChangeAcknowledgement {
	/** Their answer so far. Every change opens with it false; see {@link ImmediateDeliveryConsent}. */
	checked: boolean;
	/** The buyer's own act, in both directions. */
	onChange: (checked: boolean) => void;
}

export interface PlanChangeDialogProps {
	open: boolean;
	/** Target tier; null while the dialog is closed. */
	plan: PaidPlan | null;
	/** Which way the change goes; decided by `resolveUpgradeAction`, never re-derived here. */
	direction: PlanChangeDirection;
	status: PlanPreviewStatus;
	/** Locale-formatted prorated amount due now, once the preview resolves. */
	amount: string | null;
	/** Locale-formatted date a DOWNGRADE starts on; null for an upgrade, or when the api reported none. */
	effectiveOn: string | null;
	/**
	 * What the change states above its order button; null where it states nothing, which is a downgrade
	 * (its confirm charges nothing) or a yearly change (`planChangeOrderNote`).
	 */
	orderNote: OrderNoteFigures | null;
	/**
	 * The express request for immediate performance this change asks for, or null where it asks none: a
	 * downgrade, which charges nothing now. Which changes ask is `pay/withdrawalRight.planChangeAsksConsent`'s
	 * answer, never this view's.
	 */
	acknowledgement: PlanChangeAcknowledgement | null;
	/** Whether the order button may be pressed yet (`pay/withdrawalRight.planChangeAdmission`). */
	admission: PaymentAdmission;
	/** True while the commit is in flight — locks the actions. */
	confirming: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	onRetry: () => void;
}

/**
 * Confirm dialog for a live subscriber's in-app tier change. Props in, `theme.tokens` out — it decides
 * nothing: {@link usePricingUpgrade} owns the preview/commit orchestration, {@link planChangeCopy} owns
 * which of the two promises about money the customer reads, and `planChangeOrderNote` owns what an
 * upgrade states above its order button. While the preview is loading it shows a spinner, and a preview
 * failure offers Try again.
 *
 * An UPGRADE states the prorated amount due now (api#297); a DOWNGRADE states the date the lower plan
 * starts and that nothing is charged (api#1047), then, where the lower plan has a day count, which transfers it
 * shortens (`planChangeCopy.window`); pressing Confirm under that sentence is recorded with the change. Both
 * end in the primary Confirm with a quiet Cancel under it, and so does the preview's error state with Try
 * again.
 *
 * An upgrade's Confirm charges at once, so it is an order button, and it names the action and the amount it
 * charges, "Upgrade for €4.20" (owner decision 2026-10-09, kitecorp/transferify-ui#3394). Above it sit the
 * new plan's main characteristic, the same line the coupon claim dialog states (Art. 246a § 1 Abs. 1 Nr. 1
 * EGBGB), and one note, what the plan costs a month from then on, VAT included and cancellation, followed
 * by the Terms link. The title names the plan and the button the amount charged now, so neither is
 * repeated in the text (owner review of kitecorp/transferify-ui#3396).
 *
 * <b>Cancel sits UNDER the order button</b> (owner decision 2026-10-09, kitecorp/transferify-ui#3394), in
 * the DOM and on screen at every width, so the note and its Terms link run straight into the button with
 * nothing between them. That is what § 312j Abs. 2 BGB asks for: the information
 * immediately before the press that concludes the order. The one control after the button is a quiet
 * way out of the dialog that orders nothing, which leaves the button's labelling (§ 312j Abs. 3) and the
 * information above it intact. OLG Celle 13 U 21/26 concerned further buttons that continue the purchase
 * after a compliant one; the Elements checkout, which has no way out of its own, keeps nothing at all
 * after its button. The e2e checks allow exactly this Cancel after the button, by its catalog label.
 *
 * An upgrade also asks for the buyer's express request for immediate performance (kitecorp/transferify-ui#3457),
 * with the same control and the same words the subscription checkout uses: the upgrade is charged now and
 * concludes a new contract with its own 14 days, and without the request an early withdrawal is owed the whole
 * charge back (§ 357a Abs. 2 BGB, OUG 34/2014 art. 14 alin. (5)). The box sits under the plan's benefit line
 * and above the order note, so the note and its Terms link still run straight into the order button, and the
 * order button stays disabled until the box is ticked.
 *
 * The one thing the dialog reads itself is the contract document that link opens, as every order surface
 * does, and like every order surface its order button waits for that answer (ui#2521). Nothing takes
 * focus on its own when an upgrade opens, so MUI focuses the dialog, as it does for the coupon claim: the
 * order button charges at once, so a reader pressing Enter must not place an order, and a screen reader
 * starts at the dialog and reaches the note before the button. A downgrade's "Confirm switch" charges
 * nothing and keeps its autofocus.
 */
export function PlanChangeDialog(
	{open, plan, direction, status, amount, effectiveOn, orderNote, acknowledgement, admission, confirming, onConfirm, onCancel,
		onRetry}: PlanChangeDialogProps,
) {
	const {pricing, checkout, offerLadder, common, errors} = useMessages();
	const {locale} = useLocale();
	const planName = plan ? pricing.planNames[plan] : "";
	const copy = planChangeCopy({direction, planName, amount, effectiveOn, windowDays: planChangeWindowDays(plan)}, pricing.changePlan);
	// Read wherever the dialog states the Terms or asks for the request, whose "How it works" link opens them.
	const contract = useCheckoutDocument(open && (orderNote !== null || acknowledgement !== null));
	return (
		<BaseDialog title={copy.title} open={open} onClose={onCancel} maxWidth="xs">
			{status === "loading" && (
				<State role="status">
					<CircularProgress size={20} aria-hidden/>
					<span>{pricing.changePlan.calculating}</span>
				</State>
			)}
			{status === "error" && (
				<>
					<Body role="alert">{errors.loadFailed}</Body>
					<Actions>
						<ConfirmButton onClick={onRetry}>{common.retry}</ConfirmButton>
						<QuietButton onClick={onCancel}>{common.cancel}</QuietButton>
					</Actions>
				</>
			)}
			{status === "ready" && (
				<>
					{copy.body !== null && <Body>{copy.body}</Body>}
					{copy.window !== null && <Body>{copy.window}</Body>}
					{(orderNote !== null || acknowledgement !== null) && (
						<OrderNoteSlot>
							{orderNote !== null && orderNote.maxHostedUploadBytes !== null && (
								<Benefit>{offerLadder.gift.benefit(formatBytes(orderNote.maxHostedUploadBytes, locale, 0))}</Benefit>
							)}
							{acknowledgement !== null && (
								<ImmediateDeliveryConsent
									effect={withdrawalEffectOf("subscription")}
									checked={acknowledgement.checked}
									onChange={acknowledgement.onChange}
									contractDocument={contract.document}
									awaitingDocument={contract.awaitingDocument}
									testId="plan-change-consent"
								/>
							)}
							{orderNote !== null && (
								<OrderTermsLine
									text={orderNote.monthlyPrice === null ? checkout.order.note : checkout.order.notePriced(orderNote.monthlyPrice)}
									contractDocument={contract.document}/>
							)}
						</OrderNoteSlot>
					)}
					<Actions>
						<ConfirmButton onClick={onConfirm}
							disabled={confirming || contract.awaitingDocument || admission === "acknowledgementRequired"}
							autoFocus={direction === "downgrade"}>
							{copy.confirm}
						</ConfirmButton>
						<QuietButton onClick={onCancel} disabled={confirming}>{common.cancel}</QuietButton>
					</Actions>
				</>
			)}
		</BaseDialog>
	);
}
